import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import STRINGS from '../constants';

/**
 * MFAErrorMessage - Generic error/message display component for MFA flows
 * 
 * Handles different message types with appropriate styling:
 * - 'code_already_pending': Green background (informational)
 * - Other errors: Red background (serious errors)
 * 
 * @param {string} error - Error code or message to display
 * @param {object} style - Additional inline styles to merge
 * @param {boolean} useTypography - Whether to wrap text in Typography component (default: false)
 * @param {string} typographyStyle - Custom styles for Typography when useTypography is true
 * @param {number} mb - Margin bottom value (default: 0)
 */
const MFAErrorMessage = ({ 
	error, 
	style = {}, 
	useTypography = false, 
	typographyStyle = {},
	mb = 0,
	...boxProps 
}) => {
	if (!error) return null;

	// Determine styling based on error type
	const isCodePending = error === 'code_already_pending';

	const baseStyle = {
		backgroundColor: isCodePending ? '#00B000' : '#AC145A',
		borderRadius: '5px',
		color: '#FFFFFF',
		...style,
	};

	const messageText = STRINGS?.[error] || error;

	return (
		<Box
			p={4}
			mb={mb}
			style={baseStyle}
			role='alert'
			aria-live='assertive'
			{...boxProps}
		>
			{useTypography ? (
				<Typography style={{ fontSize: '14px', ...typographyStyle }}>
					{messageText}
				</Typography>
			) : (
				messageText
			)}
		</Box>
	);
};

MFAErrorMessage.propTypes = {
	error: PropTypes.string,
	style: PropTypes.object,
	useTypography: PropTypes.bool,
	typographyStyle: PropTypes.object,
	mb: PropTypes.number,
};

export default MFAErrorMessage;
